import React, { useCallback, useEffect, useState } from 'react';
import { Button, Typography, LayoutContent, LayoutContentCenter, LayoutControls, ContentFrame, ScreenContainer, DotLoader, Box, } from '@pinwheel/origami';
import { useSelector } from 'react-redux';
import ModalHeader from '../../components/ModalHeader';
import useLinkNavigate from '../../hooks/useLinkNavigate';
import { useAppDispatch, useAppSelector } from '../../state';
import { initializeMultiSupplierEligibility, selectIsEligibleForAdp, selectMatchedPlatforms, selectProcessingEligibilityCheck, selectSessionStatusResponse, } from '../../state/slices/slice-link-api';
import { useOpts } from '../../context';
import { selectExperiments, selectLinkParams, } from '../../state/slices/slice-link-params';
import { useGetLocalizedString } from '../../hooks/useGetLocalizedString';
import { pollEligibilityChecks } from '../../utils/poll-eligibility-checks';
import { setSelectedPlatform } from '../../state/slices/slice-provider-entity';
import shouldSkipPartnerBased from '../../utils/should-skip-partner-based';
import { setUserSkippedEligibilityLookup } from '../../state/slices/slice-supplier-eligibility';
import usePartnerBasedFallbackRoute from '../../LinkApiRouter/hooks/usePartnerBasedFallbackRoute';
import { isAmazon } from '../../consts/platforms';
import { checkHackyWorkspace } from '../../utils/hacky-workspace-customizations';
export default function MultiSupplierLookup() {
    var _a;
    var _b = useState(false), showSkip = _b[0], setShowSkip = _b[1];
    var _c = useState(true), isInitializing = _c[0], setIsInitializing = _c[1];
    var _d = useOpts(), linkToken = _d.linkToken, modalSessionId = _d.modalSessionId, uniqueUserId = _d.uniqueUserId;
    var linkParams = useAppSelector(selectLinkParams);
    var navigate = useLinkNavigate();
    var dispatch = useAppDispatch();
    var isProcessing = useAppSelector(selectProcessingEligibilityCheck);
    var sessionStatusResponse = useAppSelector(selectSessionStatusResponse);
    var matchedPlatforms = useAppSelector(selectMatchedPlatforms);
    var getLocalizedString = useGetLocalizedString();
    var partnerBasedFallbackRoute = usePartnerBasedFallbackRoute();
    var isEligibleForAdp = useSelector(selectIsEligibleForAdp);
    var experiments = useSelector(selectExperiments);
    var handleSkip = useCallback(function () {
        dispatch(setUserSkippedEligibilityLookup(true));
        if (matchedPlatforms.length === 0) {
            navigate(partnerBasedFallbackRoute);
        }
        else {
            navigate('/multi-supplier-eligibility/results');
        }
    }, [dispatch, matchedPlatforms.length, navigate, partnerBasedFallbackRoute]);
    useEffect(function () {
        void dispatch(initializeMultiSupplierEligibility({
            linkToken: linkToken,
            modalSessionId: modalSessionId || '',
            uniqueUserId: uniqueUserId,
            sdk: linkParams.sdk || '',
            sdkVersion: "".concat(linkParams.version.major, ".").concat(linkParams.version.minor, ".").concat(linkParams.version.patch),
        })).then(function () {
            setIsInitializing(false);
            pollEligibilityChecks();
        });
        var buttonTimeout = setTimeout(function () {
            setShowSkip(true);
        }, (process.env.IS_CI ? 1 : 8) * 1000);
        var skipTimeout = setTimeout(function () {
            handleSkip();
        }, 20 * 1000);
        return function () {
            clearTimeout(buttonTimeout);
            clearTimeout(skipTimeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(function () {
        if (shouldSkipPartnerBased(linkParams.linkTokenPayload)) {
            navigate(partnerBasedFallbackRoute);
        }
    }, [linkParams.linkTokenPayload, navigate, partnerBasedFallbackRoute]);
    useEffect(function () {
        if (sessionStatusResponse) {
            dispatch(setSelectedPlatform(sessionStatusResponse.platform));
            navigate('/link-api/login');
        }
        else if (!isInitializing && !isProcessing) {
            // If token platform ID is Amazon + user is eligible for ADP, send to Amazon login
            if (experiments.eligibilityCheckSendToAmazon &&
                isEligibleForAdp &&
                isAmazon(linkParams.linkTokenPayload.platformId)) {
                navigate('/link-api/login');
                return;
            }
            navigate('/multi-supplier-eligibility/results');
        }
    }, [
        dispatch,
        isInitializing,
        isProcessing,
        navigate,
        sessionStatusResponse,
        isEligibleForAdp,
        linkParams.linkTokenPayload.platformId,
        experiments.eligibilityCheckSendToAmazon,
    ]);
    var isRobinhood = checkHackyWorkspace('Robinhood', (_a = linkParams === null || linkParams === void 0 ? void 0 : linkParams.linkTokenPayload) === null || _a === void 0 ? void 0 : _a.workspaceName);
    return (React.createElement(ScreenContainer, { "data-testid": "mse-lookup" },
        React.createElement(ModalHeader, null),
        React.createElement(ContentFrame, null,
            React.createElement(LayoutContent, null,
                React.createElement(LayoutContentCenter, null,
                    React.createElement(Box, { sx: {
                            marginBottom: '32px',
                            display: 'flex',
                            justifyContent: 'center',
                        } },
                        React.createElement(DotLoader, null)),
                    React.createElement(Typography, { variant: isRobinhood ? 'body' : 'header', align: "center", color: "title", weight: "normal", sx: { marginBottom: '8px' } }, getLocalizedString('MS_ELIGIBILITY_LOOKUP_TEXT')),
                    !isRobinhood && (React.createElement(Typography, { variant: "caption", align: "center", color: {
                            type: 'theme',
                            colorLegacy: 'Neutral/70',
                            colorNext: 'neutral/default',
                        }, weight: "normal" }, getLocalizedString('MS_ELIGIBILITY_LOOKUP_SUBTEXT'))))),
            React.createElement(LayoutControls, null, showSkip && (React.createElement(Button, { "data-testid": "mse-skip", eventId: "MSE-skip", variant: "ghost", onClick: function () { return handleSkip(); } }, getLocalizedString('MS_ELIGIBILITY_SKIP')))))));
}
